import Link from "next/link";
import React from "react";
import { Container } from "react-bootstrap";
import Layout from "../components/layout";
import ServerProps from "../lib/serverProps";

const EquityCrowdfundingPage = ({ state }) => (
  <Layout
    title="Equity Crowdfunding - What is it?"
    meta={{
      card: "summary_large_image",
      description:
        "Learn more about the benefits and risks of equity crowdfunding for investors and founders.",
    }}
    state={state}
  >
    <div className="offset-header service-page">
      <Container className="pt-5 container-wrp">
        <h1>Equity Crowdfunding - What is it?</h1>
        <hr />
        <div className="service-content-wrapper">
          <p>
            Equity crowdfunding is a fast-growing segment of the alternative
            finance industry. It enables individuals to invest in early-stage
            and scale-up businesses. Traditionally such opportunities were
            mainly accessible to wealthy people, angel investors and venture
            capitalists. However, in 2017{" "}
            <a
              target="_blank"
              href="https://asic.gov.au/regulatory-resources/financial-services/crowd-sourced-funding/"
            >
              legislation
            </a>{" "}
            for equity crowdfunding was passed in Australia. The industry has
            grown significantly since then. As a result, platforms such as
            Stride Equity present you with{" "}
            <Link href="/invest">
              <a target="_blank">vetted investment opportunities</a>
            </Link>{" "}
            previously not accessible to the public. But how does equity
            crowdfunding work in Australia?
          </p>
          <h4>Equity Crowdfunding in Australia</h4>
          <p>
            Crowd-sourced funding (CSF) is a financial service. Therefore,
            intermediaries such as Stride Equity must hold an Australian
            Financial Services Licence (AFSL). The licence allows us to present
            potential investors like you with vetted investment opportunities.
          </p>
          <p>
            Most Australian residents who are 18 years and older can invest in
            startups, scale-ups and other growing private Australian businesses
            presented by us. We will guide you through the process when you
            create a profile.
          </p>
          <p>Investors are categorised into two groups:</p>
          <ol>
            <li>
              <b>Retail investors</b> : Can invest up to $10,000 per company per
              12-month period. Retail Investors have a 5 business day{" "}
              <Link href="/faq">
                <a target="_blank">cooling-off period</a>
              </Link>
              . During this period, they can cancel the investment for any
              reason. After the cooling-off period, retail investors are unable
              to withdraw their investment.
            </li>
            <li>
              <b>Wholesale Investors</b> : Can invest more than $10,000 per year
              per company in Australia. Investors need to provide evidence to
              confirm their wholesale investor status and have no cooling-off
              period. Wholesale investors will need to provide an accountant’s
              certificate confirming they have income of at least $250,000 for
              each of the past two years or have or control more than $2.5
              million in net assets.
            </li>
          </ol>
          <p>
            All <b>companies</b> who want to raise funds and publish their CSF
            offer must meet the following criteria:
          </p>
          <ol>
            <li>
              The company must be a proprietary company or a public company
              limited by shares;
            </li>
            <li>Have its principal place of business in Australia;</li>
            <li>
              Have a majority of its directors ordinarily residing in Australia;
            </li>
            <li>
              Not exceed the assets and annual revenue caps of $25 million;
            </li>
            <li>
              Not be listed on a financial market in Australia or overseas;
            </li>
            <li>
              Not have a substantial purpose of investing in other companies,
              entities or schemes;
            </li>
            <li>
              Must not raise more than $5 million in any 12-month period through
              CSF offers.
            </li>
          </ol>
          <p>
            If you are a company interested in raising capital, please{" "}
            <Link href="/submit">
              <a target="_blank">submit your application</a>
            </Link>
            . We will get in touch with you shortly.
          </p>
          <h3>
            What are the benefits and disadvantages of equity crowdfunding?{" "}
          </h3>
          <h4>Pros and cons of Equity Crowdfunding for investors</h4>
          <p>
            Investing in startups and scale-up companies is exciting. However,
            you should be aware of the risks and rewards before investing. Some
            of the upsides are:
          </p>
          <ul>
            <li>
              <b>The next big thing</b>: Many of the companies we present you
              challenge the status quo by innovating and disrupting industries.
              Who knows, you may be backing the next Aussie Unicorn!
            </li>
            <li>
              <b>Invest in the things you love</b>: As part of the crowd, you
              can invest in the businesses you believe in and love.
            </li>
            <li>
              <b>Investing for everyone</b>: The minimum investment amount
              varies for every opportunity. However, the lower investment
              minimums make opportunities more accessible to the public and
              allow you to build a more diversified investment portfolio.
            </li>
            <li>
              <b>Access to investment experience and expertise</b>: We must
              conduct certain due diligence checks. We, however, go beyond the
              basic checks and use our over 30 years of experience and expertise
              to dive deep into every business before publishing their offer on
              Stride Equity. As a result, we only present you with opportunities
              we think have the potential to succeed.{" "}
            </li>
            <li>
              <b>Potential high returns</b>: Investing in early-stage companies
              is risky but with high risks come potential high returns if the
              business is successful.{" "}
            </li>
          </ul>
          <p>
            As you can see, investing in startups and scale-ups can lead to
            significant gains. But, as a responsible investor, you should also
            consider the risks. Some of the downsides are:
          </p>
          <ul>
            <li>
              <b>Greater risk of failure</b>: Keep in mind that not every
              business will be successful. Investments in early-stage companies
              are at high risk due to a high degree of uncertainty. That is why
              you may lose all invested funds. Therefore, always read the{" "}
              <Link href="/general-csf-risk-warning">
                <a target="_blank">general CSF risk warning</a>
              </Link>{" "}
              and the provided Offer Document.
            </li>
            <li>
              <b>Illiquidity</b>: Investments in startups are considered
              illiquid since it may take years until you make your return on
              investment. Illiquid assets are, in general, harder to sell.
            </li>
            <li>
              <b>Limited information</b>: The company and we provide you with
              all required information and additional insights. However, we do
              not provide you with any financial, investment, legal or tax
              advice or recommendations. Therefore, you should conduct your own
              assessments of any investment opportunity and seek independent
              legal and financial advice before committing to any investment.
            </li>
            <li>
              <b>General CSF risk warning</b>: Crowd-sourced funding is risky.
              Issuers using this facility include new or rapidly growing
              ventures. Investment in these types of ventures is speculative and
              carries high risks. You may lose your entire investment, and you
              should be in a position to bear this risk without undue hardship.
              Even if the company is successful, the value of your investment
              and any return on the investment could be reduced if the company
              issues more shares. Your investment is unlikely to be liquid. This
              means you are unlikely to be able to sell your shares quickly or
              at all if you need the money or decide that this investment is not
              right for you. Even though you have remedies for misleading
              statements in the offer document or misconduct by the company, you
              may have difficulty recovering your money. There are rules for
              handling your money. However, if your money is handled
              inappropriately or the person operating the platform on which this
              offer is published becomes insolvent, you may have difficulty
              recovering your money. Ask questions, read all information given
              carefully, and seek independent financial advice before committing
              yourself to any investment.
            </li>
          </ul>
          <h3>
            How can you make a return on investment by investing in early-stage
            businesses.
          </h3>
          <h4>Return on investment for investors</h4>
          <p className="p-4">
            Every startup has its own exit strategy. Furthermore, the strategy
            may change over time. However, the most common strategies are:{" "}
          </p>
          <ul>
            <li>
              <b>Merger, acquisition or disposal</b>: A merger is when two
              businesses decide to merge both entities into a single entity. The
              objective is often to benefit from a greater scale. If a business
              chooses to purchase a majority stake in another company and takes
              control, that's called an acquisition. Many startups exit via
              merger or disposal.
            </li>
            <li>
              <b>Initial public offering (IPO)</b>: IPOs are also a popular exit
              strategy once a company has reached a certain size. Many
              Australian startups want to list on the Australian Securities
              Exchange (ASX). After a business is listed, existing shareholders
              can easily buy and sell their shares.
            </li>
            <li>
              <b>Share buyback</b>: A business may decide to buy back shares
              from its investors.
            </li>
            <li>
              <b>Other options:</b> Companies that reach profitability may
              choose to pay dividends to their shareholders. However, it usually
              takes a few years until businesses start to pay dividends. Private
              Secondary Markets allow private company shareholders to buy and
              sell shares. Two individuals can also decide to exchange shares
              for cash privately as long as it is in accordance with the
              relevant shareholder agreement.
            </li>
          </ul>

          <p className="p-4">
            As you can see, the benefits of equity crowdfunding are pretty
            attractive for investors as long as you are aware of the involved
            risks. <br />
            <br /> That leaves us wondering why a business should choose to
            raise capital through the crowd?
          </p>

          <h4>Pros and cons of Equity Crowdfunding for startups</h4>
          <p className="p-4">
            Building and scaling a business consists of many challenges. Raising
            capital is just one of the challenges you face during your journey
            as an entrepreneur. Some of the advantages of equity crowdfunding
            for founders are:
          </p>

          <ul>
            <li>
              <b>Access to capital</b>: Many startups fail as they can't access
              angels, venture capital and bank loans. Equity crowdfunding
              provides founders with an alternative option, the crowd. The crowd
              represents a bigger group of investors who generally invest
              smaller sums. This approach can be more efficient than convincing
              a few high-net-worth individuals and/or venture capitalists.
            </li>
            <li>
              <b>Brand awareness and advocates</b>: Equity Crowdfunding will
              require you to market your business through your channels and
              networks. Therefore, the campaign will increase brand awareness,
              is likely to attract new customers, and lead to media coverage.
              Furthermore, people who believe in your business and decide to
              invest are also likely to become brand advocates. They will often
              promote your business and products and services within their
              networks.
            </li>
            <li>
              <b>Access a larger pool of potential investors:</b> Stride Equity
              crowd is industry and stage agnostic. Our investor crowd is open
              to all sorts of businesses and always looking for the next big
              thing!
            </li>
            <li>
              <b>Transparent costs:</b> You can check our{" "}
              <Link href="/faq">
                <a target="_blank">fees and costs</a>
              </Link>{" "}
              online and reach out to us.
            </li>
            <li>
              <b>Experience and expertise:</b> We have more than 30 years of
              experience raising capital, investing in early and late-stage
              businesses and advisory services! This vast amount of experience
              and expertise is accessible to you!
            </li>
          </ul>

          <p>
            The upsides of equity crowdfunding for startups are significant, but
            there are also a few downsides to consider. Some of the
            disadvantages are:
          </p>

          <ul>
            <li>
              <b>Failure</b>: An unsuccessful campaign may reduce the confidence
              of the public and investors in the business and affect your
              reputation. That is why we have a comprehensive due diligence
              process.
            </li>
            <li>
              <b>Too early</b>: You will need to build and activate your crowd
              before launching an offer to increase your success chances.
            </li>
            <li>
              <b>Wrong valuation</b>: Valuating a business, especially an
              early-stage company, is a science and an art at the same time.
              Luckily for you, our team has decades of experience in it!
            </li>
            <li>
              <b>Wrong choice</b>: We know that equity crowdfunding is a very
              tempting option, but it may not be the right option for your
              business.
            </li>
          </ul>

          <p>
            As you can see, equity crowdfunding can help you to propel your
            business in many ways with the right partner and timing. The final
            question that remains is, how does it work?
          </p>

          <h4>Equity Crowdfunding for investors</h4>

          <p>Investing in startups is simple. All you have to do is:</p>

          <ul>
            <li>
              <b>Check out our current investment opportunities</b>: Feel free
              to explore all open{" "}
              <Link href="/invest">
                <a target="_blank">EOIs and Offers</a>
              </Link>
              . If you see one or more you like, you need to open an account
              with us.
            </li>
            <li>
              <b>Ask questions</b>: Now that you have a profile, you can read
              all provided documents and ask the startup questions. We highly
              encourage you to use the provided communication facility to ask
              questions and conduct your own research. Before investing, you
              must read the offer document and acknowledge all{" "}
              <Link href="/general-csf-risk-warning">
                <a target="_blank">risk warnings</a>
              </Link>
              .
            </li>
            <li>
              <b>Click on invest and verify your identity</b>: If it is your
              first investment on our platform, we will ask you to verify your
              identity and wholesale status (if applicable). After that, you can
              proceed and make your first investment.
            </li>
            <li>
              <b>Payment</b>: We will provide you with the details and notify
              you once we have received your payment.
            </li>
            <li>
              <b>Campaign outcome:</b> If the offer reaches at least its minimum
              target, we will declare the campaign a success and provide further
              information regarding your allocation and the share registry.
            </li>
            <li>
              <b>Congratulations:</b> You have become a shareholder and,
              therefore, a partial owner of the business. You can support the
              company by sharing news and updates with your networks.
              Furthermore, you will now receive regular updates on the company's
              performance.
            </li>
          </ul>
          <p>
            Investing in startups was never as easy as today. You may continue
            to invest in more companies you believe in.
          </p>

          <h4>Equity Crowdfunding for startups</h4>
          <p>
            It is simple for potential investors to invest in your business. We
            streamlined the process for you so you can focus on what you do
            best. All you have to do is:
          </p>

          <ul>
            <li>
              <b>Submit application</b>: You must create an account and{" "}
              <Link href="/submit">
                <a target="_blank">submit your application</a>
              </Link>
              . It will take you no more than a few minutes. We will then review
              your application and provide you with initial feedback. If we
              think it's a mutual match, we will schedule a meeting with you to
              learn more about you, your team and your business.
            </li>
            <li>
              <b>Due diligence</b>: We will conduct a series of checks and ask
              you to sign a hosting agreement.
            </li>
            <li>
              <b>Investment committee decision</b>: Our investment committee
              will decide whether or not we want to invest in your business. We
              will notify you about the outcome as soon as possible.
            </li>
            <li>
              <b>Expression of interest (EOI) campaign</b>: We will publish your
              EOI campaign once we have made a decision to invest in your
              company. This campaign aims to evaluate investors' interests and
              get their feedback. We will analyse the outcome of the campaign
              and discuss it with you. If the interest is high enough, we will
              publish your CSF offer.
            </li>
            <li>
              <b>Offer campaign</b>: Now, you have to go out to the market and
              showcase your business and investment opportunity. It is your time
              to get into the spotlight and shine! We will assist you as much as
              we can.
            </li>
            <li>
              <b>Campaign end</b>: You achieved your fundraising goal! We will
              assist with administrative tasks and facilitate the next steps
              with you.
            </li>
            <li>
              <b>Receive funds</b>: Once we have received all the funds, and the
              cooling-off period is over, we will transfer the funds to you. You
              can now focus on what you do best and build your business. If you
              need an additional capital injection later, do not hesitate to
              reach out again!
            </li>
          </ul>
          <p>
            Launching an equity crowdfunding campaign requires commitment,
            however, you will gain much more than just additional funds with the
            proper preparation and partner.
            <p>
              You will have a community of investors that may also be your
              customers helping you achieve your vision faster. Ready to launch
              a campaign? Then,{" "}
              <Link href="/submit">
                <a target="_blank">submit your application now</a>
              </Link>
              .
            </p>
          </p>
          <h4>Do you have more questions?</h4>
          <p>
            Below you will find some additional resources. Please do not
            hesitate to{" "}
            <Link href="/contact">
              <a target="_blank">contact us</a>
            </Link>{" "}
            if you have further questions.
            <br />
            <br /> We are looking forward to hearing from you!
          </p>
        </div>
      </Container>
    </div>
  </Layout>
);

export async function getServerSideProps({ req, res }) {
  return {
    props: {
      state: await ServerProps.getState(req),
    },
  };
}

export default EquityCrowdfundingPage;
